import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import {
  addMinutes,
  differenceInCalendarDays,
  differenceInMinutes,
  isSameDay,
  parseISO,
} from 'date-fns';
import { app } from '@ircf/config';

/**
 * Timezone-correct date handling.
 *
 * The rule this module enforces: instants cross the wire as UTC ISO strings and
 * are *only* converted to local wall-clock time at the point of display, using
 * the configured business timezone rather than the visitor's device timezone.
 *
 * A coach in Sydney and a client travelling in London must see the same session
 * time, so `Date#toLocaleString` is banned in components - an ESLint rule
 * enforces it - and every format goes through a helper here.
 */

export const BUSINESS_TIMEZONE = app.timezone;
const LOCALE = app.locale;

/** Parses a UTC ISO-8601 instant. Throws on an invalid value rather than
 * silently producing `Invalid Date`, which would render as "NaN". */
export function parseInstant(iso: string): Date {
  const parsed = parseISO(iso);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(`Invalid ISO-8601 instant: "${iso}"`);
  }
  return parsed;
}

/** `Mon 14 Apr` */
export function formatDateShort(iso: string): string {
  return formatInTimeZone(parseInstant(iso), BUSINESS_TIMEZONE, 'EEE d MMM');
}

/** `Monday 14 April 2026` */
export function formatDateLong(iso: string): string {
  return formatInTimeZone(parseInstant(iso), BUSINESS_TIMEZONE, 'EEEE d MMMM yyyy');
}

/** `6:00 am` */
export function formatTime(iso: string): string {
  return formatInTimeZone(parseInstant(iso), BUSINESS_TIMEZONE, 'h:mm a').toLowerCase();
}

/** `Mon 14 Apr, 6:00 am` */
export function formatDateTime(iso: string): string {
  return `${formatDateShort(iso)}, ${formatTime(iso)}`;
}

/** `6:00 am - 7:00 am` */
export function formatTimeRange(startIso: string, endIso: string): string {
  return `${formatTime(startIso)} - ${formatTime(endIso)}`;
}

/** `Mon 14 Apr, 6:00 am - 7:00 am`, collapsing the date when it is shared. */
export function formatDateTimeRange(startIso: string, endIso: string): string {
  const start = parseInstant(startIso);
  const end = parseInstant(endIso);
  const sameDay = isSameDay(
    toZonedTime(start, BUSINESS_TIMEZONE),
    toZonedTime(end, BUSINESS_TIMEZONE),
  );

  if (sameDay) {
    return `${formatDateShort(startIso)}, ${formatTime(startIso)} - ${formatTime(endIso)}`;
  }
  return `${formatDateTime(startIso)} - ${formatDateTime(endIso)}`;
}

/** `YYYY-MM-DD` in the business timezone, for date-only API parameters. */
export function toIsoDate(value: Date | string): string {
  const date = typeof value === 'string' ? parseInstant(value) : value;
  return formatInTimeZone(date, BUSINESS_TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Calendar arithmetic on date-only values.
 *
 * A `YYYY-MM-DD` has no timezone: "the 17th" is the 17th regardless of where it is
 * read. So calendar maths is done on a UTC anchor and formatted back out of UTC,
 * never via `toZonedTime`.
 *
 * That distinction is not academic. Converting a date-only value into the business
 * timezone and then formatting it *back* into that timezone applies the offset
 * twice, which silently shifts the result by a day - but only on a machine whose
 * local timezone differs from the business one. The bug is therefore invisible on
 * the coach's laptop in Sydney and appears in CI, in Docker and for a client
 * travelling abroad.
 */
function anchorOf(isoDate: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) throw new Error(`Invalid ISO date (expected YYYY-MM-DD): "${isoDate}"`);
  const [, year, month, day] = match;
  // Midday, so no arithmetic can drift across a boundary via a DST offset.
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));
}

/** `YYYY-MM-DD` from a UTC calendar anchor. */
function anchorToIsoDate(anchor: Date): string {
  return anchor.toISOString().slice(0, 10);
}

/** The current instant as a UTC ISO string, for payloads that record "now". */
export function nowInstant(): string {
  return new Date().toISOString();
}

/** Today's calendar date in the business timezone. */
export function todayIsoDate(): string {
  return toIsoDate(new Date());
}

/**
 * The business-timezone date a number of hours from now. Used for the earliest
 * date a booking form will accept, derived from the minimum-notice rule.
 */
export function isoDateInHours(hours: number): string {
  return toIsoDate(new Date(Date.now() + hours * 60 * 60 * 1000));
}

/** The business-timezone date a number of days from now. */
export function isoDateInDays(days: number): string {
  return isoDateInHours(days * 24);
}

/**
 * Converts a business-timezone wall-clock date and time into a UTC instant.
 * Used when the booking form collects "14 April, 6:00 am" and the API needs the
 * corresponding instant.
 */
export function wallClockToInstant(isoDate: string, localTime: string): string {
  return fromZonedTime(`${isoDate}T${localTime}:00`, BUSINESS_TIMEZONE).toISOString();
}

/** `in 3 days`, `in 2 hours`, `12 minutes ago`, `just now`. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const target = parseInstant(iso);
  const minutes = differenceInMinutes(target, now);
  const absolute = Math.abs(minutes);

  if (absolute < 1) return 'just now';

  if (absolute < 60) {
    const unit = absolute === 1 ? 'minute' : 'minutes';
    return minutes > 0 ? `in ${absolute} ${unit}` : `${absolute} ${unit} ago`;
  }

  if (absolute < 60 * 24) {
    const hours = Math.round(absolute / 60);
    const unit = hours === 1 ? 'hour' : 'hours';
    return minutes > 0 ? `in ${hours} ${unit}` : `${hours} ${unit} ago`;
  }

  const days = Math.abs(differenceInCalendarDays(target, now));
  if (days === 1) return minutes > 0 ? 'tomorrow' : 'yesterday';
  if (days < 30) return minutes > 0 ? `in ${days} days` : `${days} days ago`;

  return formatDateShort(iso);
}

/** `1 h 15 min`, `45 min`. */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours} h` : `${hours} h ${remainder} min`;
}

/** Inclusive day list for a calendar view. */
export function calendarRange(
  anchorIsoDate: string,
  view: 'day' | 'threeDay' | 'week' | 'month' | 'agenda',
): { from: string; to: string } {
  const anchor = anchorOf(anchorIsoDate);

  switch (view) {
    case 'day':
      return { from: anchorToIsoDate(anchor), to: anchorToIsoDate(anchor) };
    case 'threeDay':
      return {
        from: anchorToIsoDate(anchor),
        to: anchorToIsoDate(addDaysUtc(anchor, 2)),
      };
    case 'week':
      return {
        from: anchorToIsoDate(startOfWeekUtc(anchor)),
        to: anchorToIsoDate(addDaysUtc(startOfWeekUtc(anchor), 6)),
      };
    case 'month': {
      // Padded to whole weeks so the month grid has no ragged leading or trailing row.
      const first = new Date(
        Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1, 12),
      );
      const last = new Date(
        Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0, 12),
      );
      return {
        from: anchorToIsoDate(startOfWeekUtc(first)),
        to: anchorToIsoDate(addDaysUtc(startOfWeekUtc(last), 6)),
      };
    }
    case 'agenda':
      return { from: anchorToIsoDate(anchor), to: anchorToIsoDate(addDaysUtc(anchor, 30)) };
  }
}

/** Monday-start week, Australian business convention. */
function startOfWeekUtc(anchor: Date): Date {
  const day = anchor.getUTCDay();
  const offset = day === 0 ? -6 : 1 - day;
  return addDaysUtc(anchor, offset);
}

function addDaysUtc(anchor: Date, days: number): Date {
  return new Date(
    Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), anchor.getUTCDate() + days, 12),
  );
}

/** Adds months, clamping to the last day of a shorter target month. */
function addMonthsUtc(anchor: Date, months: number): Date {
  const year = anchor.getUTCFullYear();
  const month = anchor.getUTCMonth() + months;
  const day = anchor.getUTCDate();
  const daysInTarget = new Date(Date.UTC(year, month + 1, 0, 12)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day, daysInTarget), 12));
}

/**
 * Moves a calendar anchor one period in either direction, matching the period
 * the current view actually displays.
 */
export function shiftCalendarAnchor(
  anchorIsoDate: string,
  view: 'day' | 'threeDay' | 'week' | 'month' | 'agenda',
  direction: -1 | 1,
): string {
  const anchor = anchorOf(anchorIsoDate);

  switch (view) {
    case 'day':
      return anchorToIsoDate(addDaysUtc(anchor, direction));
    case 'threeDay':
      return anchorToIsoDate(addDaysUtc(anchor, 3 * direction));
    case 'week':
      return anchorToIsoDate(addDaysUtc(anchor, 7 * direction));
    case 'month':
      return anchorToIsoDate(addMonthsUtc(anchor, direction));
    case 'agenda':
      return anchorToIsoDate(addDaysUtc(anchor, 30 * direction));
  }
}

/** Inclusive list of calendar dates between two `YYYY-MM-DD` bounds. */
export function eachIsoDateBetween(fromIsoDate: string, toIsoDate_: string): string[] {
  const dates: string[] = [];
  let cursor = anchorOf(fromIsoDate);
  const last = anchorOf(toIsoDate_);

  // Bounded so a malformed range cannot spin: no calendar view spans a year.
  for (let guard = 0; guard < 400 && cursor.getTime() <= last.getTime(); guard += 1) {
    dates.push(anchorToIsoDate(cursor));
    cursor = addDaysUtc(cursor, 1);
  }

  return dates;
}

/** `Mon`, for calendar column headings. */
export function formatWeekdayShort(isoDate: string): string {
  return formatInTimeZone(anchorOf(isoDate), 'UTC', 'EEE');
}

/** `14`, the day of the month, for calendar cells. */
export function formatDayOfMonth(isoDate: string): string {
  return formatInTimeZone(anchorOf(isoDate), 'UTC', 'd');
}

/** `April 2026`, for the calendar heading. */
export function formatMonthYear(isoDate: string): string {
  return formatInTimeZone(anchorOf(isoDate), 'UTC', 'MMMM yyyy');
}

/** `Mon 14 Apr` from a date-only value. */
export function formatIsoDateShort(isoDate: string): string {
  return formatInTimeZone(anchorOf(isoDate), 'UTC', 'EEE d MMM');
}

/** Weekday index for a date-only value, 0 = Sunday. */
export function weekdayOfIsoDate(isoDate: string): number {
  return anchorOf(isoDate).getUTCDay();
}

/** True when the instant has already passed. */
export function isPast(iso: string, now: Date = new Date()): boolean {
  return parseInstant(iso).getTime() < now.getTime();
}

/** Hours remaining until the instant; negative once it has passed. */
export function hoursUntil(iso: string, now: Date = new Date()): number {
  return differenceInMinutes(parseInstant(iso), now) / 60;
}

/** Adds minutes to an instant and returns a UTC ISO string. */
export function addMinutesToInstant(iso: string, minutes: number): string {
  return addMinutes(parseInstant(iso), minutes).toISOString();
}

/** Weekday index in the business timezone, 0 = Sunday. */
export function weekdayOf(iso: string): number {
  return toZonedTime(parseInstant(iso), BUSINESS_TIMEZONE).getDay();
}

/** Short timezone label for display, e.g. `AEST`. */
export function timezoneLabel(iso: string = new Date().toISOString()): string {
  return formatInTimeZone(parseInstant(iso), BUSINESS_TIMEZONE, 'zzz');
}

/** Locale-aware number formatting with fixed fraction digits. */
export function formatNumber(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}
